import BasePage from './BasePage.js';
import calculateInheritance from '../utilities/calculateInheritance.js';
import renderFamilyTree, { preloadIcons } from '../utilities/renderFamilyTree.js';
import DiagnosisReportApi from '../api/DiagnosisReportApi.js';

/**
 * 診断結果ページ
 */
export default class ResultPage extends BasePage {
  #ratioDisplayMode = 'percent';
  #legalResult = null;      // 法定相続の計算結果を保持
  #customRatios = null;     // カスタム割合の計算結果を保持
  #previousFamily = null;   // 前回の家族構成を保持（変更検出用）
  #contactBtnHandler;
  #applyRatioBtnHandler;    // 割合再計算ボタンのハンドラーを保持
  #diagnosisReportApi = new DiagnosisReportApi();  // 診断レポート送信API
  #familyTreeSvg = null;    // 家系図SVG要素を保持

  /**
   * 数値と単位を分離して、単位を小さいフォントで表示
   * @param {string} value - "5000" や "50" のような値
   * @param {string} unit - 単位（'万円', '円', '%'）
   * @returns {string} HTML文字列（例: "5000<span class=\"unit ms-1\">万円</span>"）
   */
  #formatWithSmallUnit(value, unit, className = '') {
    const valueHtml = className ? `<span class="${className}">${value}</span>` : value;
    return `${valueHtml}<span class="unit ms-1">${unit}</span>`;
  }

  async render() {
    try {
      // 法定相続の計算結果を保存
      this.#legalResult = calculateInheritance(this._app.data.assets, this._app.data.family);

      // 家族構成が変更された場合のみ、古いカスタム割合をクリア
      // （相続人の数が変わっている可能性があるため）
      if (this.#hasFamilyChanged()) {
        this.#customRatios = null;
      }

      // 現在の家族構成を保存
      this.#previousFamily = JSON.parse(JSON.stringify(this._app.data.family));

      const html = this.#buildHTML();
      this._container.innerHTML = html;
      this._initializeButtonGroups();
      this.bindEvents();

      // 家系図SVGを生成して挿入
      await this.#renderFamilyTree();

      // 診断結果をapp.dataに保存（InquiryPageで使用）
      this.#storeDiagnosisResult();

      // 診断結果を事務所に自動送信
      await this.#sendDiagnosisReport();
    } catch (error) {
      console.error('ResultPage.render() エラー:', error);
      alert(`診断結果の表示中にエラーが発生しました:\n${error.message}\n\n前のページに戻ってやり直してください。`);
      throw error; // エラーを再スロー
    }
  }

  bindEvents() {
    this.#bindRatioToggle();
    this.#bindNavigationButtons();
    this.#bindCustomRatioForm();
  }

  #buildHTML() {
    const { grossTotalAssets, heirs, totalTax } = this.#legalResult;
    const { hasSpouseHeir } = heirs;

    return `
      <div class="mb-8">
        <div class="progress h-10px mb-3">
          <div class="progress-bar bg-primary" role="progressbar" style="width: 100%"
               aria-valuenow="100" aria-valuemin="0" aria-valuemax="100"></div>
        </div>
      </div>

      <div class="text-center mb-8">
        <h2 class="fw-bold mb-3 fs-3x">診断が完了しました</h2>
        <p class="fs-5 mb-0">総資産は <span class="fs-1 fw-bold text-primary">${grossTotalAssets.toLocaleString()}万円</span> です</p>
      </div>

      <div class="mb-6">
        <div class="section-title-bg py-3 px-4 mb-2 fw-bold fs-6 mx-n3 mx-md-0 text-primary">家族構成図</div>
        <div class="text-center" data-ref="familyTreeContainer">
          <!-- 家系図SVGがここに挿入されます -->
        </div>
      </div>

      <div class="mb-6">
        <div class="section-title-bg py-3 px-4 mb-2 fw-bold fs-6 mx-n3 mx-md-0 d-flex justify-content-between align-items-center text-primary">
          <span>相続税比率</span>
          <div class="btn-group btn-group-sm" data-kt-buttons="true" data-kt-buttons-target="[data-kt-button]" role="group">
            <label class="btn btn-sm btn-outline btn-active-primary ${this.#ratioDisplayMode === 'fraction' ? 'active' : ''}" data-kt-button="true">
              <input class="btn-check" type="radio" name="ratio_display" value="fraction" ${this.#ratioDisplayMode === 'fraction' ? 'checked' : ''} data-ratio-mode="fraction" />
              分数表示
            </label>
            <label class="btn btn-sm btn-outline btn-active-primary ${this.#ratioDisplayMode === 'percent' ? 'active' : ''}" data-kt-button="true">
              <input class="btn-check" type="radio" name="ratio_display" value="percent" ${this.#ratioDisplayMode === 'percent' ? 'checked' : ''} data-ratio-mode="percent" />
              %表示
            </label>
          </div>
        </div>

        <!-- テーブルコンテナ（この部分だけ再描画） -->
        <div data-ref="inheritanceTableContainer">
          ${this.#buildInheritanceTable()}
        </div>

        <!-- 割合指定フォーム（アコーディオン） -->
        <div class="mt-4">
          <div class="accordion" id="ratioAccordion">
            <div class="mb-5">
              <div class="d-flex justify-content-end mb-3">
                <button type="button" class="btn btn-sm collapsed" data-bs-toggle="collapse" data-bs-target="#ratioAccordionBody" style="background-color: #6a3e21; color: white; border: none;">
                  割合を指定して再計算
                  <i class="ki-duotone ki-arrow-down fs-4 ms-2"><span class="path1"></span><span class="path2"></span></i>
                </button>
              </div>
              <div id="ratioAccordionBody" class="collapse fs-6 ps-10">
                ${this.#buildRatioInputForm()}
                <div class="text-end mt-3">
                  <button type="button" class="btn btn-primary btn-sm" data-ref="applyRatioBtn" disabled>
                    この割合で再計算
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="mb-6">
        <div class="section-title-bg py-3 px-4 mb-2 fw-bold fs-6 mx-n3 mx-md-0 text-primary">注意事項</div>
        <div class="p-4 bg-light rounded border">
          <div class="mb-4">
            <h6 class="fw-bold mb-2">注意事項1　二次相続の検討</h6>
            <p class="mb-0">一次相続と二次相続の税額をシミュレーションし、最も負担の少ない方法を検討しましょう。</p>
          </div>
          <div class="mb-4">
            <h6 class="fw-bold mb-2">注意事項2　遺産分割への注意</h6>
            <p class="mb-0">遺産分割協議では、交流の少ない方とも連絡・協力が必要です。<br>トラブルを防ぐため、遺言書の作成をおすすめします。</p>
          </div>
          <div class="mb-4">
            <h6 class="fw-bold mb-2">注意事項3　贈与の活用</h6>
            <p class="mb-0">生前贈与は相続税の軽減につながります。<br>早めに行うことで、資産を有効に活用できる場合もあります。</p>
          </div>
          <div class="mb-0">
            <h6 class="fw-bold mb-2">注意事項4　小規模宅地の適用確認</h6>
            <p class="mb-0">一定の要件を満たせば、相続財産の評価を減額できます。<br>ただし、要件が厳格なため、事前に適用可否を確認しておきましょう。</p>
          </div>
        </div>
      </div>

      <div class="mb-8">
        <div class="section-title-bg py-3 px-4 mb-2 fw-bold fs-6 mx-n3 mx-md-0 text-primary">相談先情報</div>
        ${this.#buildFirmInfoCard()}
      </div>

      <div class="fixed-footer d-flex gap-3">
        <button type="button" class="btn btn-outline btn-outline-secondary" style="flex: 1 1 0;" data-ref="backBtn">
          <i class="ki-duotone ki-arrow-left fs-3 me-1">
            <span class="path1"></span>
            <span class="path2"></span>
          </i>
          戻る
        </button>
        <button type="button" class="btn btn-primary" style="flex: 1 1 0;" data-ref="contactBtn">
          結果をメールで受取る
          <i class="ki-duotone ki-arrow-right fs-3 ms-1">
            <span class="path1"></span>
            <span class="path2"></span>
          </i>
        </button>
      </div>
    `;
  }

  #bindRatioToggle() {
    const ratioModeInputs = this._container.querySelectorAll('[data-ratio-mode]');

    ratioModeInputs.forEach(input => {
      input.addEventListener('change', (e) => {
        this.#ratioDisplayMode = e.target.getAttribute('data-ratio-mode');
        // ページ全体ではなく、テーブル部分のみを再描画
        const tableContainer = this._container.querySelector('[data-ref="inheritanceTableContainer"]');
        if (tableContainer) {
          tableContainer.innerHTML = this.#buildInheritanceTable();
          // 割合入力フォームのイベントを再バインド
          this.#bindCustomRatioForm();
        }
      });
    });
  }

  #bindNavigationButtons() {
    const backBtn = this._container.querySelector('[data-ref="backBtn"]');
    const contactBtn = this._container.querySelector('[data-ref="contactBtn"]');

    backBtn?.addEventListener('click', () => {
      // 家族構成入力画面に戻る場合、カスタム割合をクリア
      this.#customRatios = null;
      this.#previousFamily = null;
      this._app.prevStep();
    });

    // 問い合わせボタンのイベントリスナーを管理（再レンダリング対応）
    if (!this.#contactBtnHandler) {
      this.#contactBtnHandler = () => {
        this._app.nextStep();
      };
    }

    contactBtn?.removeEventListener('click', this.#contactBtnHandler);
    contactBtn?.addEventListener('click', this.#contactBtnHandler);
  }

  /**
   * 家族構成が変更されたかチェック
   * @returns {boolean} 変更された場合true
   */
  #hasFamilyChanged() {
    if (!this.#previousFamily) return true; // 初回レンダリング時

    const current = this._app.data.family;
    const previous = this.#previousFamily;

    // 配偶者の状態が変わった
    if (current.spouse !== previous.spouse) return true;

    // 子供の数が変わった
    if (current.childrenCount !== previous.childrenCount) return true;

    // 各子供の状態または孫の数が変わった
    for (let i = 0; i < current.children.length; i++) {
      if (current.children[i].status !== previous.children[i].status) return true;
      if (current.children[i].grandchildren !== previous.children[i].grandchildren) return true;
    }

    // 父母の状態が変わった
    if (current.father !== previous.father) return true;
    if (current.mother !== previous.mother) return true;

    // 兄弟姉妹の数が変わった
    if (current.siblingsCount !== previous.siblingsCount) return true;

    return false; // 変更なし
  }

  /**
   * カスタム割合フォームのイベントをバインド
   */
  #bindCustomRatioForm() {
    const inputs = this._container.querySelectorAll('.ratio-input');
    const totalEl = this._container.querySelector('[data-ref="totalPercentage"]');
    const applyBtn = this._container.querySelector('[data-ref="applyRatioBtn"]');

    if (!inputs.length || !totalEl || !applyBtn) return;

    // 合計をリアルタイム表示
    const updateTotal = () => {
      let total = 0;
      inputs.forEach(input => {
        total += parseInt(input.value) || 0;
      });

      totalEl.value = total;

      // 100%の場合のみボタンを有効化
      if (total === 100) {
        applyBtn.disabled = false;
        totalEl.classList.remove('text-danger');
      } else {
        applyBtn.disabled = true;
        totalEl.classList.add('text-danger');
      }
    };

    // 入力時に合計を更新
    inputs.forEach(input => {
      input.addEventListener('input', updateTotal);
    });

    // 初期表示
    updateTotal();

    // 再計算ボタンのイベントリスナーを管理（重複登録防止）
    if (!this.#applyRatioBtnHandler) {
      this.#applyRatioBtnHandler = () => {
        this.#applyCustomRatios();
      };
    }

    applyBtn.removeEventListener('click', this.#applyRatioBtnHandler);
    applyBtn.addEventListener('click', this.#applyRatioBtnHandler);
  }

  /**
   * カスタム割合で再計算（テーブルのみ更新）
   */
  #applyCustomRatios() {
    try {
      const inputs = this._container.querySelectorAll('.ratio-input');
      const inputRatios = [];
      const ratios = {};

      // 入力値を取得
      inputs.forEach(input => {
        const index = parseInt(input.dataset.heirIndex);
        const value = parseInt(input.value) || 0;
        inputRatios[index] = value;
        ratios[index] = value / 100; // パーセントを小数に変換
      });

      // カスタム割合で再計算
      const result = calculateInheritance(
        this._app.data.assets,
        this._app.data.family,
        { customRatios: ratios }
      );

      // 結果を保存（入力値も保持）
      this.#customRatios = {
        inputRatios,  // 入力値を保持（フォーム復元用）
        ...result
      };

      // テーブル部分のみ再描画
      const tableContainer = this._container.querySelector('[data-ref="inheritanceTableContainer"]');
      if (tableContainer) {
        tableContainer.innerHTML = this.#buildInheritanceTable();
      }

      // 成功メッセージを表示
      alert('指定した割合で再計算しました');

      // テーブルまでスムーズスクロール
      if (tableContainer) {
        tableContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch (error) {
      console.error('カスタム割合の再計算エラー:', error);
      alert(`割合指定の再計算中にエラーが発生しました:\n${error.message}\n\nページをリロードしてやり直してください。`);
      throw error;
    }
  }

  /**
   * 診断結果を事務所に送信
   * レンダリング直後に自動的に呼び出され、ユーザーには通知されません。
   */
  async #sendDiagnosisReport() {
    try {
      // 事務所IDを取得（URLパラメータまたはグローバル変数から）
      const urlParams = new URLSearchParams(window.location.search);
      const firmId = parseInt(urlParams.get('firm') || '0', 10);

      if (!firmId) {
        console.warn('事務所IDが取得できませんでした。診断結果の送信をスキップします。');
        return;
      }

      // 家系図SVGを文字列に変換
      if (!this.#familyTreeSvg) {
        console.warn('家系図SVGが生成されていません。診断結果の送信をスキップします。');
        return;
      }

      const familyTreeSvgString = new XMLSerializer().serializeToString(this.#familyTreeSvg);

      // 診断日時（現在時刻）
      const diagnosisDate = new Date().toISOString();

      // 送信データを構築
      const reportData = {
        firmId,
        diagnosisDate,
        familyTreeSvg: familyTreeSvgString,
        assets: this._app.data.assets,
        result: {
          grossTotalAssets: this.#legalResult.grossTotalAssets,  // 単純合計（表示用）
          totalAssets: this.#legalResult.totalAssets,            // 非課税枠適用後（税計算用）
          heirs: this.#legalResult.heirs.heirs,
          totalTax: this.#legalResult.totalTax,
          hasSpouseHeir: this.#legalResult.heirs.hasSpouseHeir
        }
      };

      // API送信
      await this.#diagnosisReportApi.send(reportData);

      console.log('診断結果を事務所に送信しました。');
    } catch (error) {
      // エラーが発生してもユーザーには通知せず、コンソールにログ出力のみ
      console.error('診断結果の送信に失敗しました:', error);
    }
  }

  /**
   * 診断結果をapp.dataに保存
   * InquiryPageでユーザーへのPDF送信に使用
   */
  #storeDiagnosisResult() {
    if (!this.#familyTreeSvg) {
      console.warn('familyTreeSvgが存在しません');
      return;
    }

    const familyTreeSvgString = new XMLSerializer().serializeToString(this.#familyTreeSvg);
    const diagnosisDate = new Date().toISOString();

    this._app.data.diagnosisResult = {
      diagnosisDate,
      familyTreeSvg: familyTreeSvgString,
      assets: this._app.data.assets,
      result: {
        grossTotalAssets: this.#legalResult.grossTotalAssets,
        totalAssets: this.#legalResult.totalAssets,
        heirs: this.#legalResult.heirs.heirs,
        totalTax: this.#legalResult.totalTax,
        hasSpouseHeir: this.#legalResult.heirs.hasSpouseHeir
      }
    };
  }

  async #renderFamilyTree() {
    const container = this._container.querySelector('[data-ref="familyTreeContainer"]');
    if (!container) return;

    try {
      // アイコンを事前読み込み
      await preloadIcons();

      // コンテナ幅を取得
      const containerWidth = container.clientWidth || 800;

      // 相続計算結果を取得
      const result = calculateInheritance(this._app.data.assets, this._app.data.family);
      const { heirs } = result;

      // 家族構成データに相続割合（分数形式）を追加
      const familyData = this.#buildFamilyDataWithRatios(heirs.heirs);

      // 相続割合を表示（常に分数表示）
      const showRatios = true;

      // 家系図SVGを生成
      const svg = await renderFamilyTree(familyData, containerWidth, { showRatios });

      // SVGを保持（診断結果送信で使用）
      this.#familyTreeSvg = svg;

      // コンテナに挿入
      container.innerHTML = '';
      container.appendChild(svg);
    } catch (error) {
      console.error('家系図の生成に失敗しました:', error);
      container.innerHTML = '<p class="text-muted">家系図を表示できませんでした。</p>';
    }
  }

  /**
   * 相続割合データを含む家族構成データを構築（常に分数表示）
   * @param {Array} heirs - 相続人配列
   * @returns {Object} 割合データを含む家族構成データ
   */
  #buildFamilyDataWithRatios(heirs) {
    const originalFamily = this._app.data.family;
    const familyData = {
      childrenCount: originalFamily.childrenCount,
      children: [],
      father: originalFamily.father,
      mother: originalFamily.mother,
      siblingsCount: originalFamily.siblingsCount || 0
    };

    // 配偶者の割合を設定（分数形式）
    const spouseHeir = heirs.find(h => h.name === '配偶者');
    if (spouseHeir) {
      familyData.spouse = {
        status: 'alive',
        ratio: spouseHeir.ratioFraction
      };
    } else {
      familyData.spouse = originalFamily.spouse;
    }

    // 子供と孫の割合を設定（分数形式）
    originalFamily.children.forEach((child, childIndex) => {
      const childData = {
        status: child.status,
        grandchildren: child.grandchildren
      };

      if (child.status === 'alive') {
        // 健在の子供の割合を取得（分数形式）
        const childHeir = heirs.find(h => h.name === `子${childIndex + 1}`);
        if (childHeir) {
          childData.ratio = childHeir.ratioFraction;
        }
      } else if (child.grandchildren > 0) {
        // 故人の子供の場合、孫の割合を取得（分数形式）
        const grandchildHeir = heirs.find(h => h.name === `孫（子${childIndex + 1}）`);
        if (grandchildHeir) {
          childData.grandchildRatio = grandchildHeir.ratioFraction;
        }
      }

      familyData.children.push(childData);
    });

    // 父の割合を設定（分数形式）
    const fatherHeir = heirs.find(h => h.name === '父');
    if (fatherHeir) {
      familyData.fatherRatio = fatherHeir.ratioFraction;
    }

    // 母の割合を設定（分数形式）
    const motherHeir = heirs.find(h => h.name === '母');
    if (motherHeir) {
      familyData.motherRatio = motherHeir.ratioFraction;
    }

    // 兄弟姉妹の割合を設定（分数形式）
    if (familyData.siblingsCount > 0) {
      familyData.siblingRatios = [];
      for (let i = 0; i < familyData.siblingsCount; i++) {
        const siblingHeir = heirs.find(h => h.name === `兄弟${i + 1}`);
        if (siblingHeir) {
          familyData.siblingRatios.push(siblingHeir.ratioFraction);
        }
      }

      // 兄弟全体の合計割合（家系図の「兄弟N名」表示用）
      // 配偶者がいる場合は1/4、いない場合は全部
      const hasSpouseHeir = heirs.some(h => h.name === '配偶者');
      familyData.siblingTotalRatio = hasSpouseHeir ? '1/4' : '1/1';
    }

    return familyData;
  }

  /**
   * 相続税比率テーブルを生成
   * @returns {string} テーブルHTML
   */
  #buildInheritanceTable() {
    // カスタム割合がない場合は法定相続のみ
    if (!this.#customRatios) {
      return this.#buildLegalOnlyTable();
    }

    // カスタム割合がある場合は、法定相続と割合指定を別々の表として表示
    return this.#buildSeparateTables();
  }

  /**
   * 法定相続のみのテーブルを生成（1段表示）
   * @returns {string} テーブルHTML
   */
  #buildLegalOnlyTable() {
    const { totalAssets, heirs, totalTax } = this.#legalResult;
    const { hasSpouseHeir } = heirs;

    const heirsTable = heirs.heirs.map(heir => {
      const ratio = this.#ratioDisplayMode === 'fraction'
        ? `<span class="inherit-ratio">${heir.ratioFraction}</span>`
        : this.#formatWithSmallUnit(heir.ratioPercent, '%', 'inherit-ratio');
      return `
      <tr>
        <td class="fw-semibold">${heir.name}</td>
        <td class="text-end">${ratio}</td>
        <td class="text-end">${this.#formatWithSmallUnit(heir.inheritance, '万円', 'inherit-num')}</td>
        <td class="text-end">${heir.tax === '※計算中' ? heir.tax : this.#formatWithSmallUnit(heir.tax, '万円', 'inherit-num')}</td>
      </tr>
    `;
    }).join('');

    const totalInheritance = totalAssets.toLocaleString();
    const totalRatio = this.#ratioDisplayMode === 'fraction' ? '1' : '100';

    return `
      <div class="table-responsive">
        <table class="table table-row-bordered align-middle gy-3 inheritance-table">
          <thead>
            <tr>
              <th colspan="4" class="text-center fw-bold text-primary" style="background-color: #F5EDE5;">
                法定相続
              </th>
            </tr>
            <tr class="fw-bold">
              <th>相続人</th>
              <th class="text-end">割合</th>
              <th class="text-end">相続財産</th>
              <th class="text-end">相続税額</th>
            </tr>
          </thead>
          <tbody>
            ${heirsTable}
            <tr class="fw-bold border-top border-2">
              <td>合計</td>
              <td class="text-end">${this.#ratioDisplayMode === 'fraction' ? `<span class="inherit-ratio">${totalRatio}</span>` : this.#formatWithSmallUnit(totalRatio, '%', 'inherit-ratio')}</td>
              <td class="text-end">${this.#formatWithSmallUnit(totalInheritance, '万円', 'inherit-num')}</td>
              <td class="text-end">${this.#formatWithSmallUnit(totalTax, '万円', 'inherit-num')}</td>
            </tr>
          </tbody>
        </table>
      </div>
      ${hasSpouseHeir ? `
      <p class="fs-7 mb-0">※ 配偶者は配偶者控除により、法定相続分または1億6千万円まで相続税が非課税となります。</p>
      ` : ''}
      <p class="fs-7 ${hasSpouseHeir ? 'mt-2' : ''} mb-0">※ 保険金・退職金は非課税枠を適用後の金額で計算しています。</p>
    `;
  }

  /**
   * 法定相続と割合指定を別々の表として表示
   * @returns {string} テーブルHTML
   */
  #buildSeparateTables() {
    const { totalAssets } = this.#legalResult;
    const legalHeirs = this.#legalResult.heirs.heirs;
    const legalTotalTax = this.#legalResult.totalTax;
    const customHeirs = this.#customRatios.heirs.heirs;
    const customTotalTax = this.#customRatios.totalTax;
    const hasSpouseHeir = this.#legalResult.heirs.hasSpouseHeir;

    // 法定相続テーブル
    const legalHeirsRows = legalHeirs.map(heir => {
      const ratio = this.#ratioDisplayMode === 'fraction'
        ? `<span class="inherit-ratio">${heir.ratioFraction}</span>`
        : this.#formatWithSmallUnit(heir.ratioPercent, '%', 'inherit-ratio');
      return `
      <tr>
        <td class="fw-semibold">${heir.name}</td>
        <td class="text-end">${ratio}</td>
        <td class="text-end">${this.#formatWithSmallUnit(heir.inheritance, '万円', 'inherit-num')}</td>
        <td class="text-end">${heir.tax === '※計算中' ? heir.tax : this.#formatWithSmallUnit(heir.tax, '万円', 'inherit-num')}</td>
      </tr>
    `;
    }).join('');

    // 割合指定テーブル
    const customHeirsRows = customHeirs.map(heir => {
      const ratio = this.#ratioDisplayMode === 'fraction'
        ? `<span class="inherit-ratio">${heir.ratioFraction}</span>`
        : this.#formatWithSmallUnit(heir.ratioPercent, '%', 'inherit-ratio');
      return `
      <tr>
        <td class="fw-semibold">${heir.name}</td>
        <td class="text-end">${ratio}</td>
        <td class="text-end">${this.#formatWithSmallUnit(heir.inheritance, '万円', 'inherit-num')}</td>
        <td class="text-end">${heir.tax === '※計算中' ? heir.tax : this.#formatWithSmallUnit(heir.tax, '万円', 'inherit-num')}</td>
      </tr>
    `;
    }).join('');

    const totalInheritance = totalAssets.toLocaleString();
    const legalTotalRatio = this.#ratioDisplayMode === 'fraction' ? '1' : '100';
    const customTotalRatio = this.#ratioDisplayMode === 'fraction' ? '1' : '100';

    return `
      <!-- 法定相続テーブル -->
      <div class="table-responsive">
        <table class="table table-row-bordered align-middle gy-3 inheritance-table">
          <thead>
            <tr>
              <th colspan="4" class="text-center fw-bold text-primary" style="background-color: #F5EDE5;">
                法定相続
              </th>
            </tr>
            <tr class="fw-bold">
              <th>相続人</th>
              <th class="text-end">割合</th>
              <th class="text-end">相続財産</th>
              <th class="text-end">相続税額</th>
            </tr>
          </thead>
          <tbody>
            ${legalHeirsRows}
            <tr class="fw-bold border-top border-2">
              <td>合計</td>
              <td class="text-end">${this.#ratioDisplayMode === 'fraction' ? `<span class="inherit-ratio">${legalTotalRatio}</span>` : this.#formatWithSmallUnit(legalTotalRatio, '%', 'inherit-ratio')}</td>
              <td class="text-end">${this.#formatWithSmallUnit(totalInheritance, '万円', 'inherit-num')}</td>
              <td class="text-end">${this.#formatWithSmallUnit(legalTotalTax, '万円', 'inherit-num')}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 割合指定テーブル -->
      <div class="table-responsive">
        <table class="table table-row-bordered align-middle gy-3 inheritance-table">
          <thead>
            <tr>
              <th colspan="4" class="text-center fw-bold text-primary" style="background-color: #F5EDE5;">
                指定割合
              </th>
            </tr>
            <tr class="fw-bold">
              <th>相続人</th>
              <th class="text-end">割合</th>
              <th class="text-end">相続財産</th>
              <th class="text-end">相続税額</th>
            </tr>
          </thead>
          <tbody>
            ${customHeirsRows}
            <tr class="fw-bold border-top border-2">
              <td>合計</td>
              <td class="text-end">${this.#ratioDisplayMode === 'fraction' ? `<span class="inherit-ratio">${customTotalRatio}</span>` : this.#formatWithSmallUnit(customTotalRatio, '%', 'inherit-ratio')}</td>
              <td class="text-end">${this.#formatWithSmallUnit(totalInheritance, '万円', 'inherit-num')}</td>
              <td class="text-end">${this.#formatWithSmallUnit(customTotalTax, '万円', 'inherit-num')}</td>
            </tr>
          </tbody>
        </table>
      </div>
      ${hasSpouseHeir ? `
      <p class="fs-7 mb-0">※ 配偶者は配偶者控除により、法定相続分または1億6千万円まで相続税が非課税となります。</p>
      ` : ''}
      <p class="fs-7 ${hasSpouseHeir ? 'mt-2' : ''} mb-0">※ 保険金・退職金は非課税枠を適用後の金額で計算しています。</p>
    `;
  }

  /**
   * 割合入力フォームを生成
   * @returns {string} フォームHTML
   */
  #buildRatioInputForm() {
    const { heirs } = this.#legalResult.heirs;

    const inputRows = heirs.map((heir, index) => {
      // カスタム割合がある場合は保存された入力値、ない場合は法定相続割合をデフォルトに
      const defaultValue = this.#customRatios?.inputRatios?.[index] ||
                          Math.round(parseFloat(heir.ratioPercent));

      return `
        <tr>
          <td class="fw-semibold text-end align-middle" style="width: 100px;">${heir.name}</td>
          <td style="width: 140px;">
            <div class="input-group input-group-solid">
              <input type="number" class="form-control form-control-solid ratio-input text-end"
                     data-heir-index="${index}" value="${defaultValue}" min="0" max="100">
              <span class="input-group-text">%</span>
            </div>
          </td>
        </tr>
      `;
    }).join('');

    return `
      <table class="table table-borderless gy-1 mb-0">
        <tbody>
          ${inputRows}
          <tr>
            <td class="fw-bold text-end align-middle">合計</td>
            <td style="width: 140px;">
              <div class="input-group">
                <input type="number" readonly class="form-control text-end fw-bold" data-ref="totalPercentage" value="" style="background-color: transparent; border: none;">
                <span class="input-group-text fw-bold" style="background-color: transparent; border: none; color: inherit;">%</span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    `;
  }

  /**
   * 事務所情報カードのHTMLを生成
   * @returns {string} 事務所情報カードのHTML
   */
  #buildFirmInfoCard() {
    const firm = window.FIRM_DATA || {};

    return `
      <div class="card">
        <div class="card-body">
          <!-- ロゴ + 基本情報（中央配置） -->
          <div class="text-center mb-4">
            <img src="${firm.logoPath || ''}" alt="${firm.name || ''}" style="max-width: 200px; height: auto; margin-bottom: 1rem;">
            <h5 class="mb-2 fw-bold">${firm.name || ''}</h5>
            <p class="mb-1 fs-6">住所: ${firm.address || ''}</p>
            <p class="mb-0 fs-6">相続鑑定士: ${firm.representativeName || ''}</p>
            ${firm.description ? `<p class="mt-3 mb-0 fs-6">${firm.description}</p>` : ''}
          </div>

          <!-- アイキャッチ画像 -->
          <div class="mb-4">
            <img src="${firm.eyecatchPath || ''}" alt="${firm.name || ''}" class="w-100 rounded">
          </div>

          <!-- 完了メッセージ -->
          <p class="mb-4">${firm.completionMessage || ''}</p>

          <!-- 連絡先（TEL強調） -->
          <div class="text-center">
            <p class="mb-2 fs-3 fw-bold text-primary">
              TEL: <a href="tel:${firm.phone || ''}" class="text-primary" style="text-decoration: underline;">${firm.phone || ''}</a>
            </p>
            <p class="mb-0 fs-6">
              メール: <a href="mailto:${firm.email || ''}" class="text-primary" style="text-decoration: underline;">${firm.email || ''}</a>
            </p>
          </div>
        </div>
      </div>
    `;
  }
}
